import { CartErrorEvent } from '@shopify/events';

const ROOT = window.Shopify?.routes?.root ?? '/';

/**
 * Resilia product section.
 * - Gallery: native scroll-snap slides kept in sync with the thumbnail strip (no slider library).
 * - Bundle offers: selecting an offer updates the form, prices, callouts, gifts tracker, gallery
 *   image and ?count= in the URL.
 * - Delivery estimate: today + N days, e.g. "September 26th".
 * - Add to cart: posts the offer (with its selling plan) plus its free gift lines to the AJAX cart,
 *   applies the offer's discount code (if set), then refreshes and opens the cart drawer.
 *   One-time purchase adds the offer without plan or gifts.
 */
class RnPdp extends HTMLElement {
  connectedCallback() {
    this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('form.rn-pdp__form'));
    this.initGallery();
    this.initOffers();
    this.initDeliveryDate();
    this.initForm();
    this.initStickyBar();
  }

  /* ------------------------------------------------------------------ gallery */

  initGallery() {
    this.slides = this.querySelector('[data-slides]');
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    if (!this.slides || this.thumbs.length === 0) return;

    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.goTo(index)));
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

    this.index = 0;
    let frame = 0;
    this.slides.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
          if (index !== this.index) this.setActive(index);
        });
      },
      { passive: true }
    );
  }

  /** @param {number} index */
  goTo(index) {
    if (!this.slides) return;
    const count = this.thumbs.length;
    const next = (index + count) % count;
    this.slides.scrollTo({ left: next * this.slides.clientWidth, behavior: 'smooth' });
    this.setActive(next);
  }

  /** @param {number} index */
  setActive(index) {
    this.index = index;
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
    const thumb = this.thumbs[index];
    const strip = thumb?.parentElement;
    if (!thumb || !strip) return;
    const left = thumb.offsetLeft - strip.offsetLeft;
    if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: left - (strip.clientWidth - thumb.offsetWidth) / 2 });
    }
  }

  /* ------------------------------------------------------------------ offers */

  initOffers() {
    this.offers = Array.from(this.querySelectorAll('[data-offer]'));
    if (this.offers.length === 0) return;

    const heroSlideImg = this.querySelector('[data-hero-slide] img');
    const heroThumbImg = this.thumbs?.[0]?.querySelector('img');
    this.heroDefaults = [heroSlideImg, heroThumbImg].map((img) =>
      img ? { img, src: img.getAttribute('src'), srcset: img.getAttribute('srcset') } : null
    );

    this.offers.forEach((offer, index) => {
      offer.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('a')) return;
        this.selectOffer(index);
      });
      offer.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectOffer(index);
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.selectOffer((index + 1) % this.offers.length, true);
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.selectOffer((index - 1 + this.offers.length) % this.offers.length, true);
        }
      });
    });

    const fromUrl = Number(new URLSearchParams(window.location.search).get('count'));
    const initial = fromUrl >= 1 && fromUrl <= this.offers.length ? fromUrl - 1 : this.activeOfferIndex();
    this.selectOffer(initial, false, false);
  }

  activeOfferIndex() {
    const index = this.offers.findIndex((offer) => offer.classList.contains('is-active'));
    return index < 0 ? 0 : index;
  }

  get activeOffer() {
    return this.offers?.[this.activeOfferIndex()] ?? null;
  }

  /**
   * @param {number} index
   * @param {boolean} [focus]
   * @param {boolean} [updateUrl]
   */
  selectOffer(index, focus = false, updateUrl = true) {
    const offer = this.offers[index];
    if (!offer) return;

    this.offers.forEach((el, i) => {
      const active = i === index;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-checked', String(active));
      el.tabIndex = active ? 0 : -1;
    });
    if (focus) offer.focus();

    const { dataset } = offer;
    const position = index + 1;

    // Form values
    const idInput = this.form?.querySelector('input[name="id"]');
    if (idInput) idInput.value = dataset.variantId || '';
    let planInput = this.form?.querySelector('input[name="selling_plan"]');
    if (dataset.sellingPlan) {
      if (!planInput && this.form) {
        planInput = Object.assign(document.createElement('input'), { type: 'hidden', name: 'selling_plan' });
        this.form.prepend(planInput);
      }
      if (planInput) planInput.value = dataset.sellingPlan;
    } else {
      planInput?.remove();
    }

    // Prices: top row, add-to-cart button, one-time link
    const setText = (selector, value) =>
      this.querySelectorAll(selector).forEach((el) => {
        el.textContent = value || '';
        el.toggleAttribute('hidden', !value);
      });
    setText('.rn-pdp__price', dataset.price);
    setText('.rn-pdp__compare', dataset.compare !== dataset.price ? dataset.compare : '');
    setText('.rn-pdp__save > span', dataset.save);
    this.querySelector('.rn-pdp__save')?.toggleAttribute('hidden', !dataset.save);
    setText('.rn-pdp__atc-prices > span:first-child', dataset.price);
    setText('.rn-pdp__atc-compare', dataset.compare !== dataset.price ? dataset.compare : '');
    setText('.rn-pdp__sticky-price', dataset.price);
    setText('.rn-pdp__sticky-compare', dataset.compare !== dataset.price ? dataset.compare : '');

    const otp = this.querySelector('[data-otp]');
    if (otp instanceof HTMLElement) {
      otp.textContent = (otp.dataset.template || '').replace(/\[price\]/g, dataset.otpPrice || '');
      otp.toggleAttribute('disabled', dataset.available !== 'true');
    }
    this.querySelector('[data-atc]')?.toggleAttribute('disabled', dataset.available !== 'true');

    // Gifts tracker
    this.querySelectorAll('[data-unlock]').forEach((tile) => {
      tile.classList.toggle('is-locked', Number(tile.getAttribute('data-unlock')) > position);
    });

    // Gallery image for this offer
    this.heroDefaults?.forEach((entry) => {
      if (!entry) return;
      if (dataset.hero) {
        entry.img.src = dataset.hero;
        entry.img.removeAttribute('srcset');
      } else {
        if (entry.src) entry.img.setAttribute('src', entry.src);
        if (entry.srcset) entry.img.setAttribute('srcset', entry.srcset);
      }
    });
    if (dataset.hero && this.slides && this.index !== 0) this.goTo(0);

    if (updateUrl) {
      const params = new URLSearchParams(window.location.search);
      params.set('count', String(position));
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    }
  }

  /* ------------------------------------------------------------------ delivery date */

  initDeliveryDate() {
    const target = this.querySelector('[data-delivery-date]');
    if (!target) return;
    const date = new Date();
    date.setDate(date.getDate() + (Number(this.dataset.deliveryDays) || 3));
    const day = date.getDate();
    const suffix = day > 3 && day < 21 ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
    target.textContent = `${date.toLocaleString('default', { month: 'long' })} ${day}${suffix}`;
  }

  /* ------------------------------------------------------------------ cart */

  initForm() {
    if (!this.form) return;
    // The buy buttons are plain buttons (not submit), so subscription widgets that hook the
    // product form's submit can't add a second, one-time line. Enter in the form still works.
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addToCart(this.querySelector('[data-atc]'), true);
    });
    this.querySelector('[data-atc]')?.addEventListener('click', (event) =>
      this.addToCart(/** @type {HTMLElement} */ (event.currentTarget), true)
    );
    this.querySelector('[data-otp]')?.addEventListener('click', (event) =>
      this.addToCart(/** @type {HTMLElement} */ (event.currentTarget), false)
    );
  }

  /* ------------------------------------------------------------------ sticky bar */

  /**
   * Sticky bar: visible only after the buy box has scrolled up out of view (not before it).
   * Its button scrolls back to the buy box (rn-scroll-top.js), where the order is placed.
   */
  initStickyBar() {
    const bar = this.querySelector('[data-sticky]');
    const anchor = this.form;
    if (!(bar instanceof HTMLElement) || !anchor) return;

    const setShown = (shown) => {
      bar.hidden = false;
      bar.classList.toggle('is-shown', shown);
      bar.inert = !shown;
    };
    new IntersectionObserver(([entry]) => {
      if (entry) setShown(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(anchor);
    setShown(false);
  }

  /** @param {boolean} subscription */
  buildItems(subscription) {
    // Read the variant and plan from our own markup, never from the form fields: subscription
    // widgets (e.g. Seal) inject into / rewrite the product form's `selling_plan` input.
    const offer = this.activeOffer;
    const variantId = Number(offer?.dataset.variantId || this.dataset.variantId);
    const plan = Number(offer ? offer.dataset.sellingPlan : this.dataset.sellingPlan);
    const main = { id: variantId, quantity: 1 };
    if (subscription && plan) main.selling_plan = plan;

    const items = [main];
    // One-time purchase: no free gifts (matches the reference copy)
    if (subscription && offer?.dataset.gifts) {
      for (const entry of offer.dataset.gifts.split(',')) {
        const [id, quantity] = entry.split(':').map(Number);
        if (id) items.push({ id, quantity: quantity || 1 });
      }
    }
    return items;
  }

  /** @param {string} message */
  showError(message) {
    const el = this.querySelector('[data-atc-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /**
   * Adds the offer (and its free gifts) to the cart, applies the offer's discount code,
   * then refreshes the theme's cart drawer and slides it open. Falls back to the cart page
   * when the theme uses a cart page instead of a drawer.
   * @param {HTMLElement | null} button
   * @param {boolean} subscription
   */
  async addToCart(button, subscription) {
    if (!this.form || button?.classList.contains('is-loading')) return;
    const items = this.buildItems(subscription);
    const discount = subscription ? this.activeOffer?.dataset.discount : '';

    const post = (path, body) =>
      fetch(`${ROOT}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

    this.showError('');
    button?.classList.add('is-loading');
    try {
      const response = await post('cart/add.js', { items });
      const result = await response.json();

      if (!response.ok || result.status) {
        const message = result.description || result.message || 'Add to cart failed';
        this.showError(message);
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        button?.classList.remove('is-loading');
        return;
      }

      if (discount) {
        await post('cart/update.js', { discount });
      }

      // Skip the cart drawer: go straight to checkout once the bundle is in the cart
      window.location.assign(`${ROOT}checkout`);
    } catch (error) {
      this.showError('Something went wrong. Please try again.');
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      button?.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('rn-pdp-flowvea')) customElements.define('rn-pdp-flowvea', RnPdp);

/* Customer videos: tap to play inline (one at a time); arrows scroll the row by one card. */
if (!window.rnPdpUgcReady) {
  window.rnPdpUgcReady = true;

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const play = event.target.closest('[data-ugc-play]');
    if (play) {
      const video = play.parentElement.querySelector('video');
      if (!video) return;
      document.querySelectorAll('.rn-pdp__ugc-video').forEach((other) => {
        if (other !== video && !other.paused) other.pause();
      });
      video.controls = true;
      play.hidden = true;
      video.play();
      return;
    }

    const arrow = event.target.closest('[data-ugc-prev], [data-ugc-next]');
    if (arrow) {
      const row = arrow.closest('[data-ugc]').querySelector('[data-ugc-row]');
      const card = row.firstElementChild;
      const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(row).columnGap || 0) : row.clientWidth;
      row.scrollBy({ left: arrow.hasAttribute('data-ugc-next') ? step : -step, behavior: 'smooth' });
    }
  });
}
